import { motion } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { HERO, BRAND } from "@/data/content";

const word = {
  hidden: { opacity: 0, y: 60, rotateX: -40 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    rotateX: 0,
    transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.3 + i * 0.08 },
  }),
};

export const Hero = () => {
  const headlineWords = HERO.headline.split(" ");

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-screen flex flex-col items-center justify-center text-center px-6 md:px-12 lg:px-24 pt-28 pb-16 overflow-hidden"
    >
      {/* Acento decorativo */}
      <div className="pointer-events-none absolute -top-32 left-1/2 -translate-x-1/2 w-[60vw] h-[60vw] max-w-[700px] max-h-[700px] bg-[#E60000] opacity-[0.06] blur-[120px] rounded-full" />

      {/* Logo grande */}
      <motion.img
        src={BRAND.logo}
        alt={BRAND.name}
        data-testid="hero-logo"
        initial={{ opacity: 0, scale: 0.7, filter: "blur(8px)" }}
        animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
        transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
        className="w-72 md:w-[28rem] h-auto object-contain mb-6"
      />

      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.15 }}
        data-testid="hero-eyebrow"
        className="text-xs md:text-sm font-bold text-[#A3A3A3] tracking-[0.25em] uppercase mb-8 max-w-2xl"
      >
        {HERO.eyebrow}
      </motion.p>

      <h1
        data-testid="hero-headline"
        className="text-5xl md:text-7xl lg:text-[5.5rem] font-black tracking-tighter text-[#0A0A0A] max-w-5xl leading-[0.95]"
        style={{ perspective: 800 }}
      >
        {headlineWords.map((w, i) => (
          <motion.span
            key={i}
            custom={i}
            variants={word}
            initial="hidden"
            animate="visible"
            className={`inline-block mr-[0.25em] ${
              w.toLowerCase().includes("comercial") ? "text-[#E60000]" : ""
            }`}
          >
            {w}
          </motion.span>
        ))}
      </h1>

      <motion.p
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.8 }}
        data-testid="hero-subtitle"
        className="text-lg md:text-xl font-light text-[#525252] leading-relaxed max-w-2xl mx-auto mt-8"
      >
        {HERO.subtitle}
      </motion.p>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 1 }}
        className="mt-12"
      >
        <motion.a
          href="#metodologia"
          data-testid="hero-cta"
          whileHover={{ scale: 1.04 }}
          whileTap={{ scale: 0.97 }}
          className="group inline-flex items-center gap-3 bg-[#E60000] text-white px-9 py-4 font-semibold text-base md:text-lg tracking-wide hover:bg-[#CC0000] transition-colors duration-300 shadow-[0_10px_40px_-10px_rgba(230,0,0,0.5)]"
        >
          {HERO.cta}
          <ArrowDown className="w-5 h-5 group-hover:translate-y-1 transition-transform duration-300" />
        </motion.a>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.2 }}
        className="flex items-center gap-12 md:gap-20 mt-20"
      >
        {HERO.stats.map((stat) => (
          <div key={stat.label} className="text-center" data-testid={`hero-stat-${stat.label}`}>
            <div className="text-4xl md:text-5xl font-black text-[#0A0A0A] tracking-tighter">
              {stat.value}
              <span className="text-[#E60000]">{stat.suffix}</span>
            </div>
            <div className="text-xs font-bold text-[#A3A3A3] uppercase tracking-[0.15em] mt-2">
              {stat.label}
            </div>
          </div>
        ))}
      </motion.div>
    </section>
  );
};

export default Hero;
